

import { IconButton, Alert, ConfirmDialog, useToast } from "@a2z/ui";

import { useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useArchiveStoreSubscriptionPaymentMutation } from "@/modules/stores/services/mutations/useArchiveStoreSubscriptionPaymentMutation";

type Props = {
  storeId: string;
  paymentId: string;
};

export function SubscriptionPaymentDeleteButton({ storeId, paymentId }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: archivePayment } = useArchiveStoreSubscriptionPaymentMutation();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleDelete = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await archivePayment({ storeId, paymentId });
    } catch (caughtError) {
      if (caughtError instanceof ApiJsonClientError) {
        setError(caughtError.message || t(keys.errors.generic));
      } else {
        setError(t(keys.errors.generic));
      }
      setIsSubmitting(false);
      return;
    }

    toast.success(t(keys.common.updateSuccess));
    setIsSubmitting(false);
  };

  return (
    <div className="flex flex-col gap-2">
      {error ? <Alert message={error} helperText={t(keys.errors.recovery)} /> : null}
      <IconButton
        type="button"
        onClick={() => setIsDialogOpen(true)}
        disabled={isSubmitting}
        ariaLabel={t(keys.common.delete)}
        variant="danger"
        size="md"
        icon={
          isSubmitting ? (
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4 animate-spin"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="12" cy="12" r="9" className="opacity-25" />
              <path
                d="M21 12a9 9 0 0 0-9-9"
                className="opacity-75"
                strokeLinecap="round"
              />
            </svg>
          ) : (
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="h-4 w-4"
              fill="currentColor"
            >
              <path d="M8.5 3a1 1 0 0 0-1 1V5H4.75a.75.75 0 0 0 0 1.5h14.5a.75.75 0 0 0 0-1.5H16.5V4a1 1 0 0 0-1-1h-7Z" />
              <path d="M6.5 8h11l-.8 10.1a2 2 0 0 1-2 1.9H9.3a2 2 0 0 1-2-1.9L6.5 8Z" />
            </svg>
          )
        }
      />
      <ConfirmDialog
        isOpen={isDialogOpen}
        title={t(keys.admin.subscriptionPaymentDeleteConfirmTitle)}
        body={t(keys.admin.subscriptionPaymentDeleteConfirmBody)}
        confirmLabel={t(keys.common.delete)}
        cancelLabel={t(keys.common.cancel)}
        isSubmitting={isSubmitting}
        onConfirm={() => {
          setIsDialogOpen(false);
          handleDelete();
        }}
        onCancel={() => setIsDialogOpen(false)}
      />
    </div>
  );
}
